// ADR 23.9.26w §4, §6 and §8 — the queue's guarded half: enqueue (the cap,
// coalescing, the owner tag), the 30-day sweep, and the in-memory mirror the
// overlays and `PendingSyncNote` render from.
//
// **Every store call is guarded here and nowhere else.** A store that throws —
// a private window, blocked site data — makes `enqueue` resolve `false` and
// every read resolve empty, so the page behaves exactly as it did before the
// queue existed (10.9.26k: storage refusal removes the feature, never the page).
//
// The mirror holds the **owner's** readable ops only. It is refreshed by every
// enqueue and delete in this tab, and re-read from the store by
// `useOfflineQueue` on the triggers §6 names; between those a background tab's
// copy can be stale, and nothing is ever *sent* on its strength — the drainer
// and `sendOrQueue` read the store.

import { useSyncExternalStore } from 'react'

import { opExpired, opQueued, queueRefused, type QueueCause } from './analytics'
import { createIdbQueueStore, type QueueStore } from './store'
import { compareOps, isQueuedOp, opKey, type OpPayload, type QueuedOp, type StoredRecord } from './types'

/** §4 — per user; the 201st op is refused, never the oldest dropped (`pending_ops.rs`'s shape). */
const MAX_OPS_PER_USER = 200
/** §4 — an op older than this is deleted at the next open. */
export const MAX_OP_AGE_MS = 30 * 24 * 60 * 60 * 1000

let store: QueueStore = createIdbQueueStore()
let owner: string | null = null

export interface PendingSnapshot {
  owner: string | null
  ops: readonly QueuedOp[]
  /** §9 — the server's `pin_limit_reached` text from a drained list pin, shown once. */
  notice: string | null
}

let snapshot: PendingSnapshot = { owner: null, ops: [], notice: null }
const listeners = new Set<() => void>()

function publish(next: Partial<PendingSnapshot>): void {
  snapshot = { ...snapshot, ...next }
  listeners.forEach((notify) => notify())
}

/** Test seam: swap the store (suites use `__tests__/memoryStore.ts`). */
export function setQueueStore(next: QueueStore): void {
  store = next
}

/** The signed-in user whose ops may be drained, set by `useOfflineQueue` (§8). */
export function setOwner(userId: string | null): void {
  owner = userId
}

export function currentOwner(): string | null {
  return owner
}

async function safely<T>(fallback: T, work: (store: QueueStore) => Promise<T>): Promise<T> {
  try {
    return await work(store)
  } catch {
    return fallback
  }
}

/** Every stored record of one user, readable or not — the drainer applies the kind guard. */
export function listRecords(userId: string): Promise<StoredRecord[]> {
  return safely<StoredRecord[]>([], (s) => s.list(userId))
}

/** One user's readable ops, in drain order. */
export async function listOps(userId: string): Promise<QueuedOp[]> {
  const records = await listRecords(userId)
  return records.filter(isQueuedOp).sort(compareOps)
}

/** Is an op already stored under this op's coalescing key? Read from the store, never the mirror. */
export function hasPending(userId: string, payload: OpPayload): Promise<boolean> {
  return safely(false, async (s) => (await s.get(userId, opKey(payload))) !== undefined)
}

/** Re-read the mirror from the store: the owner's ops, or nothing when signed out. */
export async function refreshMirror(): Promise<void> {
  const reading = owner
  const ops = reading === null ? [] : await listOps(reading)
  if (reading === owner) publish({ owner: reading, ops })
}

async function putWithinCap(s: QueueStore, userId: string, payload: OpPayload): Promise<boolean> {
  const key = opKey(payload)
  // A put that replaces an existing key is not a new op, so it is never refused.
  const replacing = (await s.get(userId, key)) !== undefined
  if (!replacing && (await s.count(userId)) >= MAX_OPS_PER_USER) return false
  await s.put({ ...payload, userId, key, createdAt: Date.now(), rev: crypto.randomUUID() } as QueuedOp)
  return true
}

/**
 * Store one op for `userId`. Resolves `false` when refused — the cap, or a
 * store that threw — having captured `offline_queue_refused`; the caller then
 * takes today's online-only failure path.
 */
export async function enqueue(userId: string, payload: OpPayload, cause: QueueCause): Promise<boolean> {
  let stored: boolean
  try {
    stored = await putWithinCap(store, userId, payload)
  } catch {
    queueRefused('storage')
    return false
  }
  if (!stored) {
    queueRefused('cap')
    return false
  }
  opQueued(payload.kind, cause)
  await refreshMirror()
  return true
}

/** The drainer's delete: only if the record still carries the `rev` that was sent. */
export async function removeIfRev(userId: string, key: string, rev: unknown): Promise<void> {
  await safely(undefined, (s) => s.deleteIfRev(userId, key, rev))
}

/** §4 — delete every op older than 30 days, of any user, capturing `offline_op_expired` per op. */
export async function sweepExpired(now: number = Date.now()): Promise<void> {
  const expired = await safely<StoredRecord[]>([], (s) => s.purgeOlderThan(now - MAX_OP_AGE_MS))
  expired.forEach((row) => opExpired(isQueuedOp(row) ? row.kind : 'unreadable'))
}

/**
 * ADR 30.9.26i §3 — the account was deleted: its ops can never be sent, so
 * they go now rather than in 30 days. Guarded like every call here; the sweep
 * is the backstop when the store refuses.
 */
export async function purgeOwner(userId: string): Promise<void> {
  await safely(undefined, (s) => s.purgeOwner(userId))
  await refreshMirror()
}

export function setNotice(notice: string): void {
  publish({ notice })
}

export function clearNotice(): void {
  if (snapshot.notice !== null) publish({ notice: null })
}

/** W2's purge: forget the mirror and the owner. The store is never touched (§8). */
export function resetMirror(): void {
  owner = null
  publish({ owner: null, ops: [], notice: null })
}

const subscribe = (notify: () => void) => {
  listeners.add(notify)
  return () => listeners.delete(notify)
}
const read = () => snapshot

/** The mirror as a render value — the overlays' input (§6) and the pending count (§9). */
export function usePendingOps(): PendingSnapshot {
  return useSyncExternalStore(subscribe, read, read)
}

/** The same snapshot outside React (`sendOrQueue`'s "has this user ops pending?"). */
export function pendingSnapshot(): PendingSnapshot {
  return snapshot
}
